import { useState } from "react";
import produce from "immer";
import { createUseStyles } from "react-jss";
import { v4 as uuid } from "uuid";

import LinksPreview from "./LinksPreview";
import CreateHotspot from "./hotspots/CreateHotspot";
import EditHotspot from "./hotspots/EditHotspot";

const useStyles = createUseStyles({
  background: {
    width: "100%",
    height: "100%",
    position: "absolute",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    opacity: props => (props.show ? 1 : 0),
    pointerEvents: props => (props.show ? "auto" : "none"),
    transition: "opacity 0.2s ease",
  },
  container: {
    width: "90%",
    maxWidth: 700,
    backgroundColor: "#313131",
    paddingBottom: 15,
    borderRadius: 5,
    boxSizing: "border-box",
    color: "white",
    maxHeight: "calc(100vh - 30px)",
    overflowY: "auto",

    "& > *": {
      padding: [3, 15],
      "box-sizing": "border-box",
    },
  },
  newTemplate: {
    width: "calc(100% - 30px)",
    border: "none",
    padding: 15,
    textAlign: "center",
    color: "white",
    backgroundColor: "#313131",
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    borderRadius: 5,
    boxSizing: "border-box",
    margin: [0, 15],
    fontSize: 14,

    "&:hover": {
      backgroundColor: "#464646",
    },
  },
});

const getHotspot = (link, images) => {
  const scene = images[link.scene];
  if (!scene) {
    console.error("missing scene");
    return null;
  }
  const hotspot = scene.hotspots.find(x => x.uuid === link.hotspot);
  if (!hotspot) {
    console.error("missing hotspot");
    return null;
  }
  return hotspot;
};

// type Links =
//   | {
//       // template nodes appear in the links menu
//       // and can be instanciated in a scene (optional)
//       type: "template",
//       id: number,
//       show: boolean,
//       config: Hotspot,
//     }
//   | {
//       // real nodes reference a specific node
//       type: "real",
//       id: number,
//       scene: string,
//       hotspot: string,
//     };

const LinksModel = ({ show, setShowLinks, onUpdateTour, tour }) => {
  const links = tour.data.links || [];
  const [createTemplate, setCreateTemplate] = useState(false);
  const [editTemplate, setEditTemplate] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState({
    config: { options: {} },
  });
  const classes = useStyles({ show: show && !createTemplate && !editTemplate });

  const saveLinks = links =>
    onUpdateTour(
      produce(tour, draft => {
        draft.data.links ||= [];
        draft.data.links = links;
      }),
    );

  const addLink = link => {
    onUpdateTour(
      produce(tour, draft => {
        draft.data.links ||= [];
        draft.data.links.push(link);
      }),
    );
  };

  const editLink = link => {
    onUpdateTour(
      produce(tour, draft => {
        draft.data.links ||= [];
        const index = draft.data.links.findIndex(x => x.id === link.id);
        if (index >= 0) draft.data.links[index] = link;
      }),
    );
  };

  return (
    <>
      <div className={classes.background} onClick={() => setShowLinks(false)}>
        <div
          className={classes.container}
          onClick={evt => evt.stopPropagation()}
        >
          <h1>Links</h1>
          {show && links.length > 0 ? (
            links.map(link => {
              switch (link.type) {
                case "template":
                  return (
                    <LinksPreview
                      type="template"
                      key={link.id}
                      hotspot={link.config}
                      remove={() =>
                        saveLinks(links.filter(x => x.id !== link.id))
                      }
                      onClick={() => {
                        setActiveTemplate(link);
                        setEditTemplate(true);
                      }}
                    />
                  );
                case "real":
                  const hotspot = getHotspot(link, tour.data.images);
                  if (!hotspot) {
                    setTimeout(saveLinks(links.filter(x => x.id !== link.id)));
                    return null;
                  }
                  return (
                    <LinksPreview
                      type="hotspot"
                      key={link.id}
                      hotspot={hotspot}
                      remove={() =>
                        saveLinks(links.filter(x => x.id !== link.id))
                      }
                    />
                  );
                default:
                  console.error(`Link type "${link.type}" not recognised`);
                  return null;
              }
            })
          ) : (
            <p>No hotspots have been assigned to the Links menu</p>
          )}
          <button
            className={classes.newTemplate}
            onClick={() => setCreateTemplate(true)}
          >
            + New Template
          </button>
        </div>
      </div>
      <CreateHotspot
        show={createTemplate}
        onClose={() => setCreateTemplate(false)}
        onSelect={type => {
          setCreateTemplate(false);
          setEditTemplate(true);
          setActiveTemplate({ config: { type, uuid: uuid(), options: {} } });
        }}
        template
      />
      <EditHotspot
        show={editTemplate}
        hotspot={activeTemplate.config}
        type={activeTemplate.config.type}
        onClose={() => setEditTemplate(false)}
        onComplete={config => {
          if (links.map(x => x.id).includes(activeTemplate.id)) {
            editLink({
              uuid: uuid(),
              ...activeTemplate,
              config: {
                // add uuid if missing
                uuid: uuid(),
                ...config,
              },
            });
          } else {
            addLink({
              type: "template",
              id: Date.now(),
              show: true,
              config,
            });
          }
          setEditTemplate(false);
        }}
        account={tour.account}
        event={tour.team}
        template
      />
    </>
  );
};

export default LinksModel;
